import * as React from 'react';
import {RouteComponentProps, withRouter} from "react-router";
import {
    ApplicationContextConsumer,
    bindLoadingHandler,
    LoadingOverlayHandler,
    withLoadingOverlay
} from "../app/ApplicationContext";
import {IdPageUrlParams} from "../paths";
import {ErrorAlert} from "../common/ErrorAlert";
import {Attachment, ErrorResponse, Event} from "../api/dtos/dtos";
import furoApi from "../api/FuroApi";
import {setAppHeaderSubtitle} from "../app/AppHeader";
import EventForm from "./EventForm";
import ButtonBar from "../common/ButtonBar";
import locales from "../locales";
import {Button} from "antd";
import {showDeleteConfirm, showDeleteSuccessNotification, showSaveSuccessNotification} from "../utils/dialogs";
import {idFromPathParams} from "../utils/router-utils";
import {createEventDto, EventDto} from "./dtos";
import {
    AlertController,
    bindAlertController,
    GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";
import DocumentUploader from "../document-sendout/DocumentUploader";

interface EventDetailsPageState {
    editable: boolean;
    event?: EventDto;
    error?: ErrorResponse;
}

class EventDetailsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, EventDetailsPageState> {
    public readonly loadingHandler?: LoadingOverlayHandler;
    public readonly alertController?: AlertController;

    constructor(props: RouteComponentProps<IdPageUrlParams>) {
        super(props);
        this.state = {
            editable: false,
        };
    }

    public componentDidMount() {
        this.loadEvent();
    }

    public render(): React.ReactNode {
        const {editable, event, error} = this.state;

        return (
            <>
                <ApplicationContextConsumer>
                    {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
                </ApplicationContextConsumer>

                <GlobalAlertContextConsumer>
                    {(state) => bindAlertController(state.event, this)}
                </GlobalAlertContextConsumer>

                <ButtonBar style={{marginBottom: 24}}>
                    <Button
                        type="danger"
                        icon="delete"
                        onClick={this.onDelete}
                        disabled={!editable || error !== undefined}
                    >{locales.delete}</Button>
                </ButtonBar>

                <EventForm
                    editable={editable}
                    event={event}
                    onFormCancel={this.onCancel}
                    onFormSubmit={this.onSave}
                />

                {event?.id &&
                  <DocumentUploader
                    id={event.id}
                    onDelete={this.onAttachmentDelete}
                    action={this.onAttachmentUpload}
                    onLoad={this.loadAttachments}
                  />
                }

                {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
            </>
        );
    }
    
    private onCancel = () => {
        this.props.history.goBack();
    };

    private loadEvent = () => {
        const id = idFromPathParams(this.props);
        const promise = furoApi.events.get(id)
            .then(this.onEventLoaded)
            .catch(this.onApiError)
        withLoadingOverlay(promise, this.loadingHandler);
    }

    private onEventLoaded = (e: Event) => {
        const dto = createEventDto(e);
        this.setState({
            editable: true,
            event: dto,
            error: undefined,
        });
        setAppHeaderSubtitle(this.props, dto.getTitle());
    };


    private loadAttachments = (id: number): Promise<Attachment[]> => {
        return furoApi.events.getAttachments(id);
    }

    private onAttachmentDelete = (id: number, attachmentId: number): Promise<void> => {
        return furoApi.events.deleteAttachment(id, attachmentId);
    }

    private onAttachmentUpload = (id: number): string => {
        return furoApi.events.uploadAttachmentsUrl(id);
    }

    private onSave = (e: EventDto) => {
        const id = idFromPathParams(this.props);
        if (e.id !== id) {
            return;
        }

        const promise = furoApi.events.save(id, e.toEvent())
            .then(this.onEventSaved)
            .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
    };

    private onEventSaved = () => {
        this.alertController?.refreshAlertCount();
        showSaveSuccessNotification();
        this.props.history.goBack();
    }

    private onDelete = () => {
        showDeleteConfirm(this.deleteEvent);
    };

    private deleteEvent = () => {
        const id = idFromPathParams(this.props);
        const {event} = this.state;
        if (event === undefined || event.id !== id) {
            return;
        }

        const promise = furoApi.events.delete(event.id!)
            .then(this.onEventDeleted)
            .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
    };

    private onEventDeleted = () => {
        this.alertController?.refreshAlertCount();
        showDeleteSuccessNotification();
        this.props.history.goBack();
    }

    private onApiError = (error: ErrorResponse) => {
        this.setState({error});
    };
}

export default withRouter(EventDetailsPage);


// WEBPACK FOOTER //
// ./src/event-details/EventDetailsPage.tsx